"use client";

import { useState, useEffect, useCallback } from "react";
import { Text } from "@/components/ui/text";

interface CountdownTimerProps {
  expiresAt: string;
  label: string;
  onExpired: () => void;
}

function getTimeRemaining(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(diff / 1000));
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function CountdownTimer({
  expiresAt,
  label,
  onExpired,
}: CountdownTimerProps) {
  const [remaining, setRemaining] = useState(() => getTimeRemaining(expiresAt));

  const handleExpired = useCallback(() => {
    onExpired();
  }, [onExpired]);

  useEffect(() => {
    if (remaining <= 0) {
      handleExpired();
      return;
    }

    const timer = setInterval(() => {
      const next = getTimeRemaining(expiresAt);
      setRemaining(next);
      if (next <= 0) {
        clearInterval(timer);
        handleExpired();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, remaining, handleExpired]);

  if (remaining <= 0) return null;

  return (
    <Text variant="muted" className="text-sm">
      {label} {formatTime(remaining)}
    </Text>
  );
}
